import React from 'react';
import workflowUtil from '../util/workflowUtil';
import {ConfigProvider, Result, Space} from 'antd';
import {CheckCircleOutlined} from '@ant-design/icons';
import {
	OrderReviewActionButton,
	DownloadReceiptAction,
	TrackShipmentAction,
	DownloadPatientAgreementAction,
	NSAAckAction
} from '../components/ActionButtons';

/**
 * Displays the activation status panel (Complete/Incomplete) and lists any missing steps.
 *
 * @param gearOrder The Gear order
 * @param userInfo The current user
 * @param practAgreementRequired Flag indicating if the practitioner signature is needed
 * @param medicalNecessityRequired Flag indicating if Medicare Acknowlegement is needed
 * @param getValidatedPatientInfoForm Function to check for missing patient data
 */
export const ReviewStatus = ({
	gearOrder,
	userInfo,
	practAgreementRequired,
	medicalNecessityRequired,
	getValidatedPatientInfoForm
}) => {


	// Find all steps the user has access to that are not 'Completed' or 'N/A'
	let incompleteSteps = workflowUtil
		.getIncompleteUserSteps(gearOrder, userInfo)
		// Ignore the DX Codes step if this isn't an Insurance or Medicare order
		.filter((step) => step.id !== workflowUtil.workflowMetaData.diagnosis.id || isInsuranceBilling(gearOrder))
		// Ignore the Practitioner step. practAgreementRequired will cover missing practitioner info
		.filter((step) => step.id !== workflowUtil.workflowMetaData.practitioner.id)
		// Ignore the payment step because we don't really know if an amount is due. prc users will check for patientAgreement
		.filter((step) => step.id !== workflowUtil.workflowMetaData.payment.id);

	// Validate that all required patient info has been entered
	if (!incompleteSteps.find((step) => step.id === workflowUtil.workflowMetaData.patient.id)) {
		const validatedPatientInfoForm = getValidatedPatientInfoForm(gearOrder);
		if (!validatedPatientInfoForm || !Object.values(validatedPatientInfoForm).every((prop) => prop.valid)) {
			incompleteSteps.push({
				id: workflowUtil.workflowMetaData.patient.id
			});
		}
	}

	/**
	 * Add simulated steps for missing signatures.
	 * Give the simulated steps unique IDs to be used as the key when mapping <IncompleteStep> components.
	 */
	if (medicalNecessityRequired) {
		incompleteSteps.unshift({
			id: -1,
			breadcrumb: 'Acknowledgement of Medical Necessity',
			iconClassName: 'fa-edit',
			route: 'medicare'
		});
	}

	if (practAgreementRequired) {
		incompleteSteps.push({
			id: -2,
			breadcrumb: 'Practitioner Signature',
			iconClassName: 'fa-edit',
			route: 'signature'
		});
	}

	if (userInfo.type === 'prc' && !gearOrder.patientAgreement) {
		incompleteSteps.push({
			id: -3,
			breadcrumb: 'Patient/Responsible Party Acknowledgement',
			iconClassName: 'fa-edit',
			route: 'payment'
		});
	}

	const isComplete = incompleteSteps.length === 0;

	/**
	 * All incomplete steps are considered when determining if the order is complete but not all steps need to be displayed as missing steps.
	 * Filter out the steps that don't need to be displayed.
	 */
	incompleteSteps = incompleteSteps
		.filter(
			// Ignore the patient and practitioner confirmation steps
			// Only include the payment step for 'prc' users
			(step) =>
				step.id !== workflowUtil.workflowMetaData.practitioner_confirmation.id &&
				step.id !== workflowUtil.workflowMetaData.patient_confirmation.id &&
				(step.id !== workflowUtil.workflowMetaData.payment.id || userInfo.type === 'prc')
		)
		// Only include one instance of each step (filter out duplicates)
		.filter((step, index, array) => array.findIndex((step2) => step2.id === step.id) === index);

	if (isComplete) {
		return (
			<div className="row">
				<div className="col-12">
					<div className="panel panel-primary">
						<ConfigProvider
								theme={{
									token: {
										fontFamily: 'Open Sans, sans-serif',
										colorPrimary: '#275081',
									},
									components: {
										Button: {
											colorPrimary: '#337ab7;',
										},
									}
								}}
						>
						<Result
							style={{fontFamily: "Open Sans, sans-serif", padding: "15px"}}
							icon={<CheckCircleOutlined style={{color: "#275081", fontSize: "60px"}}/>}
							title={<h2 className="bold" style={{margin: '5px', marginTop: '-10px'}}>Activation Complete</h2>}
							subTitle={<p style={{color: "#777777"}}>Thank you for submitting your order.</p>}
							extra={<>
								<p style={{marginTop: "-15px", marginBottom: "10px"}}><strong>Below is your order confirmation number, please include this number on your activation card before returning your collection pack.</strong></p>
								<div>
									<h3 style={{margin: 0}}>Confirmation Number:</h3>
									<Space size="small" align="center">
										<h3 style={{padding: "0", margin: "0", fontWeight: "bold"}}>{gearOrder.id}</h3>
									</Space>
								</div>
								<div className="confirmation-activation-actions mt-10">
									<DownloadReceiptAction key="download" gearOrder={gearOrder} userInfo={userInfo} button={<OrderReviewActionButton title="View Order" />} />
									{!gearOrder.physicianInTheLoop && <DownloadPatientAgreementAction key="download-patient-agreement" gearOrder={gearOrder} userInfo={userInfo} button={<OrderReviewActionButton title="View Patient Agreement" />} />}
									{gearOrder.nsaAcknowledged && <NSAAckAction key="download-patient-nsa" gearOrder={gearOrder} userInfo={userInfo} button={<OrderReviewActionButton title="View NSA Acknowledgement" />} />}
								</div>
							</>}
						/>
						</ConfigProvider>
					</div>
				</div>
			</div>
		);
	}

	if (!gearOrder.containsRegistrationRequisition) {
		return null;
	}

	return (
		<div className="panel panel-primary" style={{ backgroundColor: '#f9cccc' }}>
			<div className="panel-body">
				<div className="text-center">
					<h2 className="mt-5">Activation Incomplete</h2>
				</div>
				{!!incompleteSteps.length && (
					<>
						<h4 className="mt-0 text-center">What's Missing?</h4>					
						<div className="row">
							<div className="col-12 col-sm-8 offset-sm-2">
								<p className="small bold text-center mb-10" style={{ color: '#234773' }}>
									Please use the links below to provide the missing information and complete your activation.
								</p>
							</div>
							<div className="col-7 col-sm-5 col-md-4 center-block" style={{ float: 'none', padding: '0' }}>
								<ul style={{ listStyle: 'none', paddingInlineStart: '20px' }}>
									{incompleteSteps.map((step) => {
										return <IncompleteStep key={step.id} step={step} />;
									})}
								</ul>
							</div>
						</div>
					</>
				)}
				<div className="text-default text-center">
					<strong>Please confirm the data is accurate and submit your order.</strong>
					<p>
						Your confirmation number will be provided when all required information has been provided
						and your activation is complete.
					</p>
				</div>
				<div className="confirmation-activation-actions">
					<TrackShipmentAction gearOrderId={gearOrder.id} trackingInformation={gearOrder.trackingInformation} button={<OrderReviewActionButton title="Track Order" />} />
				</div>
			</div>
		</div>
	);
};

/**
 * Builds a list item for the incomplete step.
 * If the step has a positive ID we get the icon, text, and route values from Workflow metadata.
 * Otherwise, we read those values from the step object.
 */
const IncompleteStep = ({ step }) => {
	let { iconClassName, breadcrumb, route } = step;
	if (step.id > 0) {
		({ iconClassName, breadcrumb, route } = workflowUtil.getWorkflowMetaDataById(step.id));
	}

	if (step.gearReqId) {
		route += `-${step.gearReqId}`;
	}

	return (
		<li>
			<button
				className="btn btn-link btn-unstyled text-default"
				style={{ fontWeight: 'bold', whiteSpace: 'normal' }}
				onClick={() => scrollToSection(`[data-step="${route}"]`)}
			>
				<i className={`fa fa-fw ${iconClassName}`} aria-hidden="true"></i>{' '}
				<span className="link-underline">{breadcrumb}</span>
			</button>
		</li>
	);
};

/**
 * Check if any requisition on the order has Insurance or Medicare billing.
 */
const isInsuranceBilling = (gearOrder) => {
	return (
		gearOrder?.requisitions?.some((req) => req.insuranceBillingSelected || req.medicareTricareBillingSelected) ??
		false
	);
};

/**
 * Scroll to an element.
 *
 * @param selector The CSS selector of the DOM element to scroll to
 */
const scrollToSection = (selector) => {
	let section = document.querySelector(selector);
	if (section) {
		window.scrollTo(0, section.getBoundingClientRect().top + window.scrollY - 125);
	}
};
